import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";

// Components
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloatingButton from "@/components/WhatsAppFloatingButton";

// Pages
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Categories from "@/pages/Categories";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import OrderTracking from "@/pages/OrderTracking";
import Wholesale from "@/pages/Wholesale";
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AuthCallback from "@/pages/AuthCallback";
import MyAccount from "@/pages/MyAccount";
import MyOrders from "@/pages/MyOrders";

// Detect Emergent OAuth callback (session_id in URL fragment) synchronously during
// render to prevent race conditions with the CustomerAuthProvider's /me check.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/categories" element={<Categories />} />
      <Route path="/products/:productId" element={<ProductDetail />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
      <Route path="/track-order" element={<OrderTracking />} />
      <Route path="/wholesale" element={<Wholesale />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />

      {/* Customer account (Google sign-in) */}
      <Route path="/account" element={<MyAccount />} />
      <Route path="/my-orders" element={<MyOrders />} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SiteSettingsProvider>
        <CustomerAuthProvider>
          <CartProvider>
            <BrowserRouter>
              <div className="min-h-screen flex flex-col bg-[#FAF8F5] text-[#1C1917]">
                <Header />
                <main className="flex-1">
                  <AppRoutes />
                </main>
                <Footer />
                <WhatsAppFloatingButton />
                <Toaster position="top-right" richColors />
              </div>
            </BrowserRouter>
          </CartProvider>
        </CustomerAuthProvider>
      </SiteSettingsProvider>
    </AuthProvider>
  );
}

export default App;
